import { useState, useEffect } from 'react';
import { MessageSquare, Send, Users, Settings as SettingsIcon, Copy, Check, ExternalLink, TestTube, Power, PowerOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface TelegramMessage {
  id: string;
  telegram_user_id: number;
  telegram_chat_id: string;
  message_type: 'incoming' | 'outgoing';
  message_text: string;
  sent_at: string;
  created_at: string;
}

interface UserWithHandsFree {
  telegram_id: number;
  telegram_username: string | null;
  first_name: string | null;
  last_name: string | null;
  preferences: {
    hands_free?: boolean;
  };
}

export default function BotManagement() {
  const [activeTab, setActiveTab] = useState<'setup' | 'chats' | 'users'>('chats');
  const [messages, setMessages] = useState<TelegramMessage[]>([]);
  const [users, setUsers] = useState<UserWithHandsFree[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [toggleLoading, setToggleLoading] = useState<number | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const webhookUrl = `${supabaseUrl}/functions/v1/telegram-webhook`;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'chats') {
      fetchMessages();
    }
  }, [activeTab, selectedUser]);

  async function fetchMessages() {
    try {
      const { data, error } = await supabase
        .from('telegram_messages')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }

  async function fetchUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('telegram_id, telegram_username, first_name, last_name, preferences')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }

  async function sendMessage() {
    if (!selectedUser || !messageText.trim()) return;

    try {
      setSending(true);
      const user = users.find(u => u.telegram_id === selectedUser);
      if (!user) return;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-telegram-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          chat_id: selectedUser,
          text: messageText,
        }),
      });

      if (response.ok) {
        await supabase.from('telegram_messages').insert({
          telegram_user_id: selectedUser,
          telegram_chat_id: selectedUser.toString(),
          message_type: 'outgoing',
          message_text: messageText,
        });

        setMessageText('');
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  }

  async function toggleHandsFree(userId: number, currentValue: boolean) {
    try {
      setToggleLoading(userId);
      setToggleError(null);

      const user = users.find(u => u.telegram_id === userId);
      const currentPreferences = user?.preferences || {};

      const { error } = await supabase
        .from('users')
        .update({
          preferences: {
            ...currentPreferences,
            hands_free: !currentValue,
          },
        })
        .eq('telegram_id', userId);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      await fetchUsers();
    } catch (error: any) {
      console.error('Error toggling hands-free:', error);
      setToggleError(error.message || 'Failed to toggle hands-free mode');
    } finally {
      setToggleLoading(null);
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const testOpenAIApiKey = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/parse-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ test: true }),
      });

      const text = await response.text();

      if (response.ok) {
        setTestResult({ success: true, message: 'OpenAI API key is valid and working!' });
      } else {
        setTestResult({ success: false, message: `Error: ${text}` });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: `Connection error: ${error.message}` });
    } finally {
      setTesting(false);
    }
  };

  const filteredMessages = selectedUser
    ? messages.filter(m => m.telegram_user_id === selectedUser)
    : messages;

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Telegram Bot Management</h2>

        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('chats')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'chats'
                ? 'bg-brand-primary text-white shadow-brand'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Chat Management
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'users'
                ? 'bg-brand-primary text-white shadow-brand'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            Users & Hands Free
          </button>
          <button
            onClick={() => setActiveTab('setup')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'setup'
                ? 'bg-brand-primary text-white shadow-brand'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
            }`}
          >
            <SettingsIcon className="w-4 h-4" />
            Bot Setup
          </button>
        </div>

        {activeTab === 'chats' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Select User</h3>
              <div className="space-y-2">
                {users.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No users yet. Users will appear here once they start using the Telegram bot.
                  </div>
                ) : (
                  users.map((user) => (
                    <button
                      key={user.telegram_id}
                      onClick={() => setSelectedUser(user.telegram_id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedUser === user.telegram_id
                          ? 'bg-brand-primary text-white'
                          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="font-medium">
                        {user.first_name} {user.last_name}
                      </p>
                      {user.telegram_username && (
                        <p className="text-sm opacity-75">@{user.telegram_username}</p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Messages</h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 h-96 overflow-y-auto space-y-3">
                {filteredMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    {selectedUser ? (
                      <p>No messages yet. Start a conversation below.</p>
                    ) : (
                      <p>Select a user to view messages</p>
                    )}
                  </div>
                ) : (
                  filteredMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-lg ${
                        msg.message_type === 'outgoing'
                          ? 'bg-brand-primary text-white ml-12'
                          : 'bg-white dark:bg-gray-700 mr-12'
                      }`}
                    >
                      <p className="text-sm">{msg.message_text}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {new Date(msg.sent_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {selectedUser && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-primary dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !messageText.trim()}
                    className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-accent disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Hands Free Settings</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enable Hands Free mode to allow automated responses via the Telegram bot for specific users.
            </p>
            {toggleError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 p-3 rounded-lg text-sm">
                {toggleError}
              </div>
            )}
            <div className="space-y-2">
              {users.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No users yet. Users will appear here once they start using the Telegram bot.
                </div>
              ) : (
                users.map((user) => (
                <div
                  key={user.telegram_id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.first_name} {user.last_name}
                    </p>
                    {user.telegram_username && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        @{user.telegram_username}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => toggleHandsFree(user.telegram_id, user.preferences?.hands_free || false)}
                    disabled={toggleLoading === user.telegram_id}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 ${
                      user.preferences?.hands_free
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {toggleLoading === user.telegram_id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {user.preferences?.hands_free ? 'Disabling...' : 'Enabling...'}
                      </>
                    ) : user.preferences?.hands_free ? (
                      <>
                        <Power className="w-4 h-4" />
                        Hands Free ON
                      </>
                    ) : (
                      <>
                        <PowerOff className="w-4 h-4" />
                        Hands Free OFF
                      </>
                    )}
                  </button>
                </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'setup' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                  1
                </span>
                <span>Create a Telegram Bot</span>
              </h3>
              <ol className="space-y-3 ml-8 text-gray-700 dark:text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    Open Telegram and search for{' '}
                    <a
                      href="https://t.me/BotFather"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium inline-flex items-center"
                    >
                      @BotFather
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    Send the command <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">/newbot</code>
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Follow the instructions to choose a name and username for your bot</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    Copy the bot token (it looks like{' '}
                    <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-xs">
                      123456789:ABCdefGHIjklMNOpqrsTUVwxyz
                    </code>
                    )
                  </span>
                </li>
              </ol>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                  2
                </span>
                <span>Configure Environment Variables</span>
              </h3>
              <div className="ml-8 space-y-4">
                <p className="text-gray-700 dark:text-gray-300">
                  Add these environment variables in your Supabase project:
                </p>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-sm font-mono text-gray-900 dark:text-white">TELEGRAM_BOT_TOKEN</code>
                    <button
                      onClick={() => copyToClipboard('TELEGRAM_BOT_TOKEN', 'token-key')}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {copied === 'token-key' ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Your Telegram bot token from @BotFather
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-sm font-mono text-gray-900 dark:text-white">OPENAI_API_KEY</code>
                    <button
                      onClick={() => copyToClipboard('OPENAI_API_KEY', 'openai-key')}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {copied === 'openai-key' ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Your OpenAI API key for receipt parsing (get it from{' '}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      OpenAI Platform
                    </a>
                    )
                  </p>
                  <button
                    onClick={testOpenAIApiKey}
                    disabled={testing}
                    className="w-full flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  >
                    <TestTube className="w-4 h-4" />
                    <span>{testing ? 'Testing...' : 'Test API Key'}</span>
                  </button>
                  {testResult && (
                    <div
                      className={`mt-3 p-3 rounded-lg text-sm ${
                        testResult.success
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                      }`}
                    >
                      {testResult.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <span className="bg-violet-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                  3
                </span>
                <span>Set Webhook URL</span>
              </h3>
              <div className="ml-8 space-y-4">
                <p className="text-gray-700 dark:text-gray-300">
                  Set the webhook URL for your bot using this command in your browser or via curl:
                </p>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Webhook URL</p>
                    <button
                      onClick={() => copyToClipboard(webhookUrl, 'webhook')}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {copied === 'webhook' ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                  <code className="text-sm font-mono text-gray-900 dark:text-white break-all block">
                    {webhookUrl}
                  </code>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
